<template>
  <div class="schedule-form">
    <!-- Planning type toggle -->
    <div class="schedule-form__type">
      <label
        class="type-option"
        :class="{ 'type-option--active': form.planningType === 'tentative' }"
      >
        <input type="radio" v-model="form.planningType" value="tentative" />
        <span class="type-option__icon">
          <BaseIcon name="edit" size="18" />
        </span>
        <span class="type-option__content">
          <span class="type-option__title">Voorlopig</span>
          <span class="type-option__desc">Nog niet bevestigd met klant</span>
        </span>
      </label>
      <label
        class="type-option"
        :class="{ 'type-option--active': form.planningType === 'confirmed' }"
      >
        <input type="radio" v-model="form.planningType" value="confirmed" />
        <span class="type-option__icon">
          <BaseIcon name="check-circle" size="18" />
        </span>
        <span class="type-option__content">
          <span class="type-option__title">Bevestigd</span>
          <span class="type-option__desc">Afspraak is bevestigd met klant</span>
        </span>
      </label>
    </div>

    <DateInput id="appointment_date" label="Datum" v-model="form.date" :error="errors.date" />

    <div class="form-row form-row--3">
      <TextInput
        id="appointment_time"
        label="Starttijd"
        v-model="form.startTime"
        type="time"
        :error="errors.startTime"
      />
      <TextInput
        id="appointment_end"
        label="Eindtijd"
        v-model="form.endTime"
        type="time"
        :error="errors.endTime"
      />
      <TextInput
        id="appointment_duration"
        label="Duur"
        :model-value="formattedDuration"
        disabled
      />
    </div>

    <SelectInput
      id="schedule_installer"
      label="Hoofdmonteur"
      v-model="form.installerId"
      :options="installerOptions"
      placeholder="Selecteer monteur..."
    />

    <MultiSelectInput
      v-if="installerOptions.length > 1"
      id="extra_installers"
      label="Extra monteurs"
      v-model="form.extraInstallerIds"
      :options="extraInstallerOptions"
      placeholder="Optioneel extra monteurs..."
    />

    <!-- Conflict warnings: blocking unless the planner explicitly overrides -->
    <div
      v-if="conflictWarnings.length"
      class="schedule-form__conflicts"
      :class="{ 'schedule-form__conflicts--blocking': !form.allowConflict }"
    >
      <div class="schedule-form__conflict-header">
        <BaseIcon name="exclamation-triangle" size="14" />
        <span>
          {{ conflictWarnings.length > 1 ? 'Dubbele boekingen' : 'Dubbele boeking' }} — monteur
          heeft dan al een afspraak
        </span>
      </div>
      <div
        v-for="(c, i) in conflictWarnings"
        :key="i"
        class="schedule-form__conflict-item"
        :class="{ 'schedule-form__conflict-item--confirmed': c.isConfirmed }"
      >
        <strong>{{ c.workOrderNumber }}</strong>
        {{ c.start }} – {{ c.end }}
        <span class="schedule-form__conflict-who">{{ c.installerName }}</span>
        <span v-if="c.isConfirmed" class="schedule-form__conflict-badge">bevestigd</span>
      </div>
      <label class="schedule-form__conflict-override">
        <input type="checkbox" v-model="form.allowConflict" />
        <span>Toch inplannen (overlap bewust toestaan)</span>
      </label>
      <p v-if="errors.conflict" class="schedule-form__conflict-error">{{ errors.conflict }}</p>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch } from 'vue';

  import DateInput from '@/components/inputs/DateInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import MultiSelectInput from '@/components/inputs/MultiSelectInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { getAppointmentStart, isAppointmentConfirmed } from '@/utils/workOrders';

  export default defineComponent({
    name: 'WorkOrderScheduleFields',

    components: {
      DateInput,
      TextInput,
      SelectInput,
      MultiSelectInput,
      BaseIcon,
    },

    props: {
      /**
       * Existing work order when rescheduling. Optional for create-flows.
       */
      workOrder: {
        type: Object,
        default: null,
      },
      installerOptions: {
        type: Array,
        default: () => [],
      },
      existingWorkOrders: {
        type: Array,
        default: () => [],
      },
      defaultPlanningType: {
        type: String,
        default: null,
        validator: (v) => v === null || v === 'tentative' || v === 'confirmed',
      },
      /**
       * Prefill duration (and end time) when creating, e.g. from product norm hours.
       * Ignored when rescheduling an existing work order.
       */
      defaultDurationMinutes: {
        default: null,
      },
    },

    setup(props) {
      const errors = ref({});
      const conflictWarnings = ref([]);

      const form = ref({
        date: null,
        startTime: '09:00',
        endTime: '11:00',
        duration: 120,
        installerId: null,
        extraInstallerIds: [],
        planningType: props.defaultPlanningType || 'tentative',
        // Overlap is a hard error server-side (409) unless explicitly allowed.
        allowConflict: false,
      });

      const calculateEndTimeFromDuration = (startTime, durationMinutes) => {
        if (!startTime || !durationMinutes) return '';
        const [h, m] = startTime.split(':').map(Number);
        const totalMin = h * 60 + m + durationMinutes;
        const endH = Math.floor(totalMin / 60) % 24;
        const endM = totalMin % 60;
        return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
      };

      const calculateDurationFromTimes = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;
        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endTime.split(':').map(Number);
        return endH * 60 + endM - (startH * 60 + startM);
      };

      watch(
        () => props.workOrder,
        (wo) => {
          if (!wo) return;

          const existingStart = getAppointmentStart(wo);
          if (existingStart) {
            const date = new Date(existingStart);
            form.value.date = date.toISOString().split('T')[0];
            form.value.startTime = date.toTimeString().slice(0, 5);
            form.value.planningType = isAppointmentConfirmed(wo) ? 'confirmed' : 'tentative';
          }
          form.value.duration = wo?.appointment_duration_minutes || 120;
          form.value.endTime = calculateEndTimeFromDuration(
            form.value.startTime,
            form.value.duration
          );
          form.value.installerId = wo?.assigned_to?.id || wo?.assigned_to || null;
          form.value.extraInstallerIds = (wo?.assigned_users || [])
            .map((u) => u.id)
            .filter((id) => id !== form.value.installerId);

          if (props.defaultPlanningType) {
            form.value.planningType = props.defaultPlanningType;
          }
        },
        { immediate: true }
      );

      watch(
        () => props.defaultDurationMinutes,
        (minutes) => {
          if (props.workOrder) return;
          const duration = Number(minutes) > 0 ? Number(minutes) : 120;
          form.value.duration = duration;
          form.value.endTime = calculateEndTimeFromDuration(form.value.startTime, duration);
        },
        { immediate: true }
      );

      const calculatedDuration = computed(() =>
        calculateDurationFromTimes(form.value.startTime, form.value.endTime)
      );

      const formattedDuration = computed(() => {
        const minutes = calculatedDuration.value;
        if (!minutes || minutes <= 0) return '00:00';
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      });

      const extraInstallerOptions = computed(() =>
        props.installerOptions.filter((opt) => opt.value !== form.value.installerId)
      );

      // If the primary installer is set/changed to someone already picked as an
      // extra installer, drop them from the extra list — prevents the same
      // installer ending up twice in assigned_user_ids.
      watch(
        () => form.value.installerId,
        (installerId) => {
          if (installerId && form.value.extraInstallerIds.includes(installerId)) {
            form.value.extraInstallerIds = form.value.extraInstallerIds.filter(
              (id) => id !== installerId
            );
          }
        }
      );

      // All technicians on a work order row: primary + helpers.
      const technicianIdsOf = (wo) => {
        const ids = new Set();
        const primary = wo?.assigned_to?.id || wo?.assigned_to;
        if (primary) ids.add(primary);
        (wo?.assigned_users || []).forEach((u) => {
          const id = typeof u === 'string' ? u : u?.id;
          if (id) ids.add(id);
        });
        (wo?.assigned_user_ids || []).forEach((id) => id && ids.add(id));
        return ids;
      };

      const installerName = (id) =>
        props.installerOptions.find((opt) => opt.value === id)?.label || 'Monteur';

      // Overlap with any other appointment of any selected technician
      // (primary or extra). Mirrors the server-side 409 guard.
      const checkConflicts = () => {
        conflictWarnings.value = [];
        if (!form.value.date || !form.value.startTime) return;
        const selected = new Set(
          [form.value.installerId, ...(form.value.extraInstallerIds || [])].filter(Boolean)
        );
        if (selected.size === 0) return;

        const newStart = new Date(`${form.value.date}T${form.value.startTime}`);
        const newEnd = new Date(
          newStart.getTime() + (form.value.duration || 120) * 60000
        );
        const fmt = (d) => d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

        props.existingWorkOrders.forEach((wo) => {
          if (props.workOrder?.id && wo.id === props.workOrder.id) return;
          if (wo.status === 'cancelled') return;
          const shared = [...technicianIdsOf(wo)].filter((id) => selected.has(id));
          if (shared.length === 0) return;

          const woDate = getAppointmentStart(wo);
          if (!woDate) return;

          const woStart = new Date(woDate);
          const woEnd = new Date(
            woStart.getTime() + (wo.appointment_duration_minutes || 120) * 60000
          );

          if (newStart < woEnd && newEnd > woStart) {
            conflictWarnings.value.push({
              workOrderNumber: wo.work_order_number,
              start: fmt(woStart),
              end: fmt(woEnd),
              installerName: shared.map(installerName).join(', '),
              isConfirmed: isAppointmentConfirmed(wo),
            });
          }
        });
      };

      const validate = () => {
        errors.value = {};

        if (!form.value.date) {
          errors.value.date = 'Datum is verplicht';
        }
        if (!form.value.startTime) {
          errors.value.startTime = 'Starttijd is verplicht';
        }
        if (!form.value.endTime) {
          errors.value.endTime = 'Eindtijd is verplicht';
        }
        if (calculatedDuration.value <= 0) {
          errors.value.endTime = 'Eindtijd moet na starttijd liggen';
        }

        checkConflicts();
        if (conflictWarnings.value.length && !form.value.allowConflict) {
          errors.value.conflict =
            'Kies een ander moment of een andere monteur, of vink "Toch inplannen" aan.';
        }
        return Object.keys(errors.value).length === 0;
      };

      const getPayload = () => ({
        date: form.value.date,
        startTime: form.value.startTime,
        duration: calculatedDuration.value,
        installerId: form.value.installerId,
        extraInstallerIds: form.value.extraInstallerIds,
        planningType: form.value.planningType,
        allowConflict: form.value.allowConflict === true,
      });

      watch(
        () => [form.value.startTime, form.value.endTime],
        () => {
          form.value.duration = calculatedDuration.value;
        },
        { immediate: true }
      );

      watch(
        () => [
          form.value.date,
          form.value.startTime,
          form.value.endTime,
          form.value.installerId,
          form.value.extraInstallerIds,
        ],
        () => {
          checkConflicts();
          if (!conflictWarnings.value.length) {
            form.value.allowConflict = false;
            delete errors.value.conflict;
          }
        },
        // immediate: een bon die nu al overlapt (oude data) toont dat meteen bij openen.
        { deep: true, immediate: true }
      );

      return {
        form,
        errors,
        conflictWarnings,
        calculatedDuration,
        formattedDuration,
        extraInstallerOptions,
        validate,
        getPayload,
      };
    },
  });
</script>

<style scoped lang="scss">
  .schedule-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__type {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }

    &__conflicts {
      padding: 0.75rem;
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      margin-top: 0.25rem;
    }

    &__conflict-header {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--amber-700);
    }

    &__conflict-item {
      font-size: 0.8125rem;
      color: var(--amber-800);
      padding: 0.25rem 0 0 1.375rem;

      &--confirmed {
        color: var(--red-700);
      }
    }

    &__conflict-badge {
      display: inline-block;
      margin-left: 0.375rem;
      padding: 0.1rem 0.375rem;
      background: var(--red-100);
      color: var(--red-700);
      border-radius: 4px;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    &__conflict-who {
      margin-left: 0.375rem;
      color: var(--slate-500);
    }

    // Zonder override is het een blokkerende fout (rood), met override een
    // bewuste waarschuwing (amber).
    &__conflicts--blocking {
      background: var(--red-50);
      border-color: var(--red-200);

      .schedule-form__conflict-header {
        color: var(--red-700);
      }
    }

    &__conflict-override {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.25rem;
      padding-left: 1.375rem;
      font-size: 0.8125rem;
      color: var(--slate-700);
      cursor: pointer;

      input {
        accent-color: var(--red-600);
      }
    }

    &__conflict-error {
      margin: 0;
      padding-left: 1.375rem;
      font-size: 0.75rem;
      color: var(--red-700);
    }
  }

  .type-option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 2px solid var(--slate-200);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--slate-300);
      background: var(--slate-50);
    }

    &--active {
      border-color: var(--blue-500);
      background: var(--blue-50);

      .type-option__title {
        color: var(--blue-700);
      }
    }

    input[type='radio'] {
      display: none;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--slate-500);
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__title {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--slate-900);
    }

    &__desc {
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.3;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;

    &--3 {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }
</style>
